import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {API} from "../../../types/API";
import {AxiosError, AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {WithdrawProvider} from "../../../types/WithdrawProvider";
import OutlineInput from "../../../components/OutlineInput";
import {Button} from "@material-tailwind/react";
import Loader from "../../../assets/icons/Loader";
import {InputType} from "../../../types/InputType";
import {setErrors, validateData} from "../../../reducers/validationReducer";
import {useDispatch} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {emailRegex, nameRegEx} from "./WithdrawsForm";
import {Input} from "../../../types/Input";
import {useTranslation} from "react-i18next";
import {APILink} from "../../../types/APILink";

interface UniPaymentFormProps {
    broker: number,
    clientTransactionID?: number
}

const UniPaymentForm = ({broker, clientTransactionID}: UniPaymentFormProps) => {

    const {t} = useTranslation();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const [amount, setAmount] = useState<number>();
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [city, setCity] = useState<string>();
    const [address, setAddress] = useState<string>();
    const [zip, setZip] = useState<string>();
    const [phone, setPhone] = useState<string>();
    const [bankName, setBankName] = useState<string>();
    const [bankIdentifier, setBankIdentifier] = useState<string>();
    const [bankCity, setBankCity] = useState<string>();
    const [bankAddress, setBankAddress] = useState<string>();
    const [accountNumber, setAccountNumber] = useState<string>();
    const [accountName, setAccountName] = useState<string>();

    const [validated, setValidated] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);

    const dispatch = useDispatch();

    const clearData = () => {
        setInProgress(false);
        setAmount(0);
        setFirstName('');
        setLastName('');
        setEmail('');
        setCity('');
        setAddress('');
        setZip('');
        setPhone('');
        setBankName('');
        setBankIdentifier('');
        setBankAddress('');
        setBankCity('');
        setAccountNumber('');
        setAccountName('');
        setValidated(false)
    }

    const getClientTransaction = useCallback(() => {
        API.getWithdrawTransaction(clientTransactionID ?? 0).then((response: AxiosResponse) => {
            setFirstName(response.data.first_name);
            setLastName(response.data.last_name);
            setEmail(response.data.email);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_TRANSACTION.replace('$transaction', (clientTransactionID ?? 0).toString())}`, toast, handleError));
    }, [toast, handleError]);


    const changeCity = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setCity(e.currentTarget.value)
        }
    }, [setCity]);

    const changeAddress = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAddress(e.currentTarget.value)
        }
    }, [setAddress]);

    const changeZip = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setZip(e.currentTarget.value)
        }
    }, [setZip]);

    const changePhone = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setPhone(e.currentTarget.value)
        }
    }, [setPhone]);

    const changeBankName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setBankName(e.currentTarget.value)
        }
    }, [setBankName]);

    const changeBankIdentifier = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setBankIdentifier(e.currentTarget.value)
        }
    }, [setBankIdentifier]);

    const changeBankCity = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setBankCity(e.currentTarget.value)
        }
    }, [setBankCity]);

    const changeBankAddress = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setBankAddress(e.currentTarget.value)
        }
    }, [setBankAddress]);


    const changeAmount = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAmount(parseFloat(e.currentTarget.value))
        }
    }, [setValidated, setAmount]);

    const changeFirstName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFirstName(e.currentTarget.value)
        }
    }, [setValidated, setFirstName]);

    const changeLastName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setLastName(e.currentTarget.value)
        }
    }, [setValidated, setLastName]);


    const changeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEmail(e.currentTarget.value)
        }
    }, [setValidated, setEmail]);

    const changeAccountNumber = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAccountNumber(e.currentTarget.value)
        }
    }, [setValidated, setAccountNumber]);

    const changeAccountName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAccountName(e.currentTarget.value)
        }
    }, [setValidated, setAccountName]);


    const handleSuccessResponse = useCallback((response: AxiosResponse) => {
        if (response.data.data) {
            clearData();
            const status = response.data.data.status;
            let message = '';
            if (status && status === 'Waiting for approve')
                message = t('admin.withdraws.approve_is_required')
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t('admin.withdraws.created_message')}<span
                className="uppercase inline-block ml-1">{message}</span></>, "bg-teal-500");
        }
    }, [toast, clearData]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        if (b) {
            const request = {
                amount,
                first_name: firstName,
                last_name: lastName,
                city,
                phone,
                address,
                zip,
                email,
                bank_city: bankCity,
                bank_identifier: bankIdentifier,
                bank_name: bankName,
                bank_address: bankAddress,
                account_number: accountNumber,
                account_name: accountName,
                provider: WithdrawProvider.UNIPAYMENT,
                broker,
            };
            API.makeUniPaymentPayWithdraw(request)
                .then((response: AxiosResponse) => handleSuccessResponse(response))
                .catch((e) => {
                    setInProgress(false);
                    if (e instanceof AxiosError && e.response) {
                        setErrors(e.response.data.errors);
                    }
                    ErrorHandler(e, `post${APILink.MAKE_UNIPAYMENT_WITHDRAW}`, toast, handleError)
                })
        } else setInProgress(false);
    }, [amount, firstName, lastName, email, city, phone, zip, address, validated, bankCity, bankIdentifier, bankName, bankAddress, accountName, accountNumber, broker, clientTransactionID]);

    const submit = useCallback(() => {
        setInProgress(true);
        const inputs: Input[] = [
            {key: 'amount', label: t('admin.withdraws.amount'), type: InputType.number, regex: '^\\d{1,18}$'},
            {key: 'first_name', label: t('admin.withdraws.first_name'), type: InputType.number, regex: nameRegEx},
            {key: 'last_name', label: t('admin.withdraws.last_name'), type: InputType.number, regex: nameRegEx},
            {key: 'phone', label: t('admin.withdraws.phone'), type: InputType.input},
            {key: 'city', label: t('admin.withdraws.city'), type: InputType.input},
            {key: 'address', label: t('admin.withdraws.address'), type: InputType.input},
            {key: 'zip', label: t('admin.withdraws.zip'), type: InputType.number, regex: '^\\d{3,10}$'},
            {key: 'email', label: t('admin.withdraws.email'), type: InputType.email, regex: emailRegex},
            {key: 'bank_city', label: t('admin.withdraws.bank_city'), type: InputType.input},
            {key: 'bank_name', label: t('admin.withdraws.bank_name'), type: InputType.input},
            {key: 'bank_identifier', label: t('admin.withdraws.bank_identifier'), type: InputType.input},
            {key: 'bank_address', label: t('admin.withdraws.bank_address'), type: InputType.input},
            {key: 'account_number', label: t('admin.withdraws.account_number'), type: InputType.input},
            {key: 'account_name', label: t('admin.withdraws.account_name'), type: InputType.input},
        ];

        const data = {
            amount,
            first_name: firstName,
            last_name: lastName,
            city,
            phone,
            address,
            zip,
            email,
            bank_city: bankCity,
            bank_identifier: bankIdentifier,
            bank_name: bankName,
            bank_address: bankAddress,
            account_number: accountNumber,
            account_name: accountName,
        };

        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));

    }, [amount, firstName, lastName, email, city, phone, zip, address, callback, bankCity, bankIdentifier, bankName, bankAddress, accountName, accountNumber]);


    const amountUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.amount_in', {currency: 'INR'})} type="number" validated={validated}
                          value={amount}
                          fieldName="amount"
                          onChange={changeAmount}/>
        </div>
    }, [validated, changeAmount, amount]);

    const firstNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.first_name')} type="text" validated={validated} value={firstName}
                          fieldName="first_name"
                          onChange={changeFirstName}/>
        </div>
    }, [validated, firstName, changeFirstName])

    const lastNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.last_name')} type="text" validated={validated} value={lastName}
                          fieldName="last_name"
                          onChange={changeLastName}/>
        </div>
    }, [validated, lastName, changeLastName])

    const emailUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.email')} type="email" validated={validated} value={email}
                          fieldName="email"
                          onChange={changeEmail}/>
        </div>
    }, [validated, email, changeEmail])

    const cityUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.city')} type="text" validated={validated} value={city}
                          fieldName="city"
                          onChange={changeCity}/>
        </div>
    }, [validated, city, changeCity])


    const addressUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.address')} type="text" validated={validated} value={address}
                          fieldName="address"
                          onChange={changeAddress}/>
        </div>
    }, [validated, address, changeAddress])

    const zipUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.zip')} type="text" validated={validated} value={zip}
                          fieldName="zip"
                          onChange={changeZip}/>
        </div>
    }, [validated, zip, changeZip])

    const phoneUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.phone')} type="text" validated={validated} value={phone}
                          fieldName="phone"
                          onChange={changePhone}/>
        </div>
    }, [validated, phone, changePhone])

    const bankNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.bank_name')} type="text" validated={validated} value={bankName}
                          fieldName="bank_name"
                          onChange={changeBankName}/>
        </div>
    }, [validated, bankName, changeBankName])

    const bankCityUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.bank_city')} type="text" validated={validated} value={bankCity}
                          fieldName="bank_city"
                          onChange={changeBankCity}/>
        </div>
    }, [validated, bankCity, changeBankCity])

    const bankAddressUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.bank_address')} type="text" validated={validated} value={bankAddress}
                          fieldName="bank_address"
                          onChange={changeBankAddress}/>
        </div>
    }, [validated, bankAddress, changeBankAddress])

    const bankIdentifierUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.bank_identifier')} type="text" validated={validated}
                          value={bankIdentifier}
                          fieldName="bank_identifier"
                          onChange={changeBankIdentifier}/>
        </div>
    }, [validated, bankIdentifier, changeBankIdentifier])

    const accountNumberUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.account_number')} type="text" validated={validated}
                          value={accountNumber}
                          fieldName="account_number"
                          onChange={changeAccountNumber}/>
        </div>
    }, [validated, accountNumber, changeAccountNumber])

    const accountNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.account_name')} type="text" validated={validated} value={accountName}
                          fieldName="account_name"
                          onChange={changeAccountName}/>
        </div>
    }, [validated, accountName, changeAccountName])


    const submitButtonUI = useMemo(() => {
        if (!amount)
            return <></>;

        return <div className="max-w-md mt-8">
            <Button className="bg-teal-500 text-white"
                    onClick={() => submit()}
                    disabled={isInProgress}>{isInProgress ?
                <Loader/> : t('Submit')}</Button>
        </div>
    }, [amount, isInProgress, submit]);


    useEffect(() => {
        clearData();
        // getCurrencyAccount();
        if (clientTransactionID) {
            getClientTransaction();
        }
    }, [broker, clientTransactionID]);

    return <>
        {firstNameUI}
        {lastNameUI}
        {emailUI}
        {phoneUI}
        {cityUI}
        {zipUI}
        {addressUI}
        {bankNameUI}
        {bankIdentifierUI}
        {bankCityUI}
        {bankAddressUI}
        {accountNumberUI}
        {accountNameUI}
        {amountUI}
        {submitButtonUI}
    </>
}

export default memo(UniPaymentForm);
